import {Box, SxProps, Typography} from '@mui/material';

import {greenColors, orangeColors} from '../../layouts/theme/designSystem';

interface Props {
  data: string[] | number[];
  sx?: SxProps;
  typographySx?: SxProps;
  onClick?: () => void;
  compact?: boolean;
  nowrap?: boolean;
  stacked?: boolean;
  /** Stack the items vertically on mobile, falling back to the row layout from `sm` up. */
  stackOnMobile?: boolean;
}

export const BoxList = ({
  data,
  sx,
  typographySx,
  onClick,
  compact = false,
  nowrap = false,
  stacked = false,
  stackOnMobile = false,
}: Props) => {
  if (!data) {
    return null;
  }

  const badgeSx: SxProps = compact
    ? {
      backgroundColor: orangeColors[3],
      borderRadius: '99px',
      padding: '4px 10px',
      ...sx,
    }
    : {
      backgroundColor: orangeColors[3],
      padding: '16px 32px',
      borderRadius: 2,
      // On mobile, stacked items span the full parent width, so drop the trailing
      // right margin (vertical spacing comes from the container gap instead).
      marginRight: stackOnMobile ? {xs: 0, sm: 3} : 3,
      ...sx,
    };

  const labelSx: SxProps = compact
    ? {
      color: greenColors[700],
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      ...typographySx,
    }
    : {
      color: greenColors[700],
      ...typographySx,
    };

  return (
    <Box
      sx={{
        alignItems: stacked ? 'flex-end' : stackOnMobile ? {xs: 'stretch', sm: 'center'} : 'center',
        display: 'flex',
        flexDirection: stacked ? 'column' : stackOnMobile ? {xs: 'column', sm: 'row'} : 'row',
        flexWrap: stacked || nowrap ? 'nowrap' : 'wrap',
        gap: compact ? '6px' : '8px',
      }}
    >
      {data.map((item) => (
        <Box key={item} onClick={onClick} sx={badgeSx}>
          <Typography variant={compact ? 'body2' : 'body1'} sx={labelSx}>
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};
